import React from 'react';
import { HeaderGroup, ColumnDef, Header } from '@tanstack/react-table';
import { Droppable, Draggable, DragDropContext, DraggableProvided } from 'react-beautiful-dnd';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import { ThemeContext } from 'styled-components';
import { Checkbox } from '@mui/material';

type CustomColumnDef<T> = ColumnDef<T> & {
  enableColumnDragging?: boolean;
};

interface HeaderGroupProps<T extends object = object> {
  headerGroup: HeaderGroup<T>;
  visibleColumns: CustomColumnDef<T>[];
  onColumnOrderChange?: (columns: string[]) => void;
  setColumnOrder: (updater: string[] | ((columnOrder: string[]) => string[])) => void;
}

const ColumnCell = <T extends object>({
  header,
  provided,
  placeholder,
}: {
  header: Header<T, unknown>;
  provided?: DraggableProvided;
  placeholder?: React.ReactNode;
}) => {
  const { primary }: any = React.useContext(ThemeContext);

  return (
    <th
      {...(provided && {
        ref: provided.innerRef,
        ...provided.draggableProps,
        ...provided.dragHandleProps,
      })}
    >
      <span onClick={header.column.getToggleSortingHandler()}>
        {typeof header.column.columnDef.header === 'function'
          ? header.column.columnDef.header(header.getContext()) // ✅ Correct way to pass header context
          : header.column.columnDef.header}
        {header.column.getIsSorted() ? (
          <span>
            {header.column.getIsSorted() === 'desc' ? <ArrowDropDownOutlinedIcon color={primary} /> : <ArrowDropUpOutlinedIcon color={primary} />}
          </span>
        ) : (
          header.column.getCanSort() && <UnfoldLessOutlinedIcon />
        )}
        {placeholder}
      </span>
    </th>
  );
};

export const HeaderGroupCell = <T extends object>({ headerGroup, visibleColumns, setColumnOrder, onColumnOrderChange }: HeaderGroupProps<T>) => {
  const currentColOrder = React.useRef<string[]>([]);

  return (
    <DragDropContext
      onDragStart={() => {
        currentColOrder.current = visibleColumns.map((col) => col.id?.toString() ?? '');
      }}
      onDragEnd={(result) => {
        if (!result.destination) return; // ✅ Prevent error if destination is null

        const colOrder = [...currentColOrder.current];
        const sIndex = result.source.index;
        const dIndex = result.destination.index;

        colOrder.splice(sIndex, 1);
        colOrder.splice(dIndex, 0, result.draggableId);
        setColumnOrder(colOrder);
        if (onColumnOrderChange) onColumnOrderChange(colOrder);
      }}
    >
      <Droppable droppableId='droppable-columns' direction='horizontal'>
        {(droppableProvided) => (
          <tr ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
            {headerGroup.headers.map((header, index) => {
              return (header.column.columnDef as CustomColumnDef<T>).enableColumnDragging ? (
                <Draggable
                  key={header.column.id?.toString() ?? index.toString()}
                  draggableId={header.column.id?.toString() ?? index.toString()}
                  index={index}
                >
                  {(provided) => <ColumnCell header={header} provided={provided} placeholder={droppableProvided.placeholder} />}
                </Draggable>
              ) : (
                <ColumnCell key={header.column.id?.toString() ?? index.toString()} header={header} />
              );
            })}
          </tr>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export const DefaultSelection = {
  id: 'selection',
  width: 40,
  header: ({ table }: { table: any }) => (
    <Checkbox
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler(),
      }}
      style={{ padding: 0 }}
    />
  ),
  cell: ({ row }: { row: any }) => (
    <Checkbox
      {...{
        checked: row.getIsSelected(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler(),
      }}
      style={{ padding: 0 }}
    />
  ),
};
